<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useMediaQuery } from '@vueuse/core';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import {
  useDisplaySenderName,
  useGroupParticipantColor,
  useHideSellerName,
} from '../useDisplaySenderName.js';
import {
  isGroupConversation,
  stripGroupSenderPrefix,
} from '../groupParticipant.js';
import { describeQuotedMessage } from 'dashboard/helper/quotedMessagePreview';
import LottieStickerCanvas from './LottieStickerCanvas.vue';
import QuotedMediaIcon from '../QuotedMediaIcon.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useGuardedMedia } from 'dashboard/composables/useGuardedMedia';
import { useI18n } from 'vue-i18n';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
  hideSenderName: { type: Boolean, default: false },
  senderNameAbove: { type: String, default: '' },
  showSenderName: { type: Boolean, default: false },
  // Aceleres 2026-08-17: a bolha de TEXTO tambem passa a mostrar quem enviou,
  // como ja acontecia com imagem, video, gif, figurinha e documento. E uma
  // marca separada de `showSenderName` de proposito: aquela carrega o estilo
  // das bolhas de midia, que nao tem padding proprio e por isso deslocam o
  // rotulo (ver senderNameClass). A bolha de texto ja tem padding, entao entra
  // so na CONDICAO de exibir; a cor do participante e compartilhada abaixo.
  textSenderName: { type: Boolean, default: false },
  mediaCaption: { type: Boolean, default: false },
  statusOnMedia: { type: Boolean, default: false },
  // Aceleres 2026-08-19: a bolha abre o lugar do horario na PROPRIA LINHA do
  // texto (um espacador invisivel no fim do conteudo, ver Text/Index.vue), e
  // nao numa faixa por baixo. E o desenho do WhatsApp: com a faixa, cada
  // mensagem ficava ~20px mais alta do que precisava — medido nos dois
  // tamanhos de fonte da raiz, em mensagem curta, longa e nos dois sentidos.
  inlineMetaSpacer: { type: Boolean, default: false },
});

defineOptions({ inheritAttrs: false });

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  contentAttributes,
  conversationId,
  id: messageId,
  createdAt,
  sender,
} = useMessageContext();

const attrs = useAttrs();

const conversationGetter = useMapGetter('getConversationById');
const currentAccount = useMapGetter('getCurrentAccount');

// Aceleres: a ponte WAME marca o grupo em `custom_attributes` do contato, mas
// esta checagem só olhava `additional_attributes` — que vem vazio. Com isso a
// conversa de grupo era classificada como individual e o nome do remetente
// nunca era exibido, deixando a autoria de cada fala só no prefixo cravado no
// texto. A resolução agora vive em groupParticipant.js, junto do resto da
// identidade de grupo.
const isWhatsAppGroup = computed(() =>
  isGroupConversation(conversationGetter.value(conversationId.value))
);

// Aceleres: a resolução vive em useDisplaySenderName para que toda bolha use a
// mesma regra — inclusive o álbum de stickers, que monta o próprio rótulo.
const displaySenderName = useDisplaySenderName();
const participantColor = useGroupParticipantColor(displaySenderName);

// Caixa Pessoais: a mensagem de saída não leva o nome do vendedor. O gate
// precisa ser explícito aqui — `sender.name` continua existindo, então testar
// só o nome resolvido deixaria a `div` do rótulo no ar, vazia e com margem.
const hideSellerName = useHideSellerName();

// O nome exibido pode vir do participante do grupo, e não do `sender` — em
// grupo o `sender` é o contato do grupo inteiro. Exigir `sender.name` aqui
// esconderia o rótulo justamente quando ele é mais necessário.
const isAttachmentReply = computed(
  () =>
    Boolean(inReplyTo?.value) &&
    [
      'image',
      'image-album',
      'video',
      'gif',
      'attachment',
      'audio',
      'sticker',
      'sticker-album',
      'embed',
      'ig-story',
    ].includes(attrs['data-bubble-name'])
);

const shouldShowSenderName = computed(
  () =>
    !hideSellerName.value &&
    !isAttachmentReply.value &&
    displaySenderName.value &&
    (props.showSenderName ||
      props.textSenderName ||
      (isWhatsAppGroup.value && orientation.value === ORIENTATION.LEFT))
);

// Aceleres: o nome do remetente é o título da bolha (aparece acima de
// imagem, vídeo e documento), então usa 16,2px nos dois sentidos da
// conversa. O corpo de texto — mensagem e legenda — fica em 15,8px, em
// `.prose-bubble p` e na regra das legendas (aceleres-overrides.css).
const senderNameClass = computed(() => {
  if (!props.showSenderName || orientation.value !== ORIENTATION.RIGHT) {
    return 'min-w-0 max-w-full truncate text-[16.2px] font-semibold';
  }

  return 'relative left-1.5 right-auto min-w-0 max-w-full truncate px-[5px] pt-0 pb-[5px] !text-[16.2px] !font-bold';
});

// Aceleres 2026-09-09: o nome fecha com dois pontos ("Facundo:") em TODAS as
// bolhas. Ate aqui era so na de texto (decisao de 17/08, em que nas midias o
// nome contava como titulo da bolha e ficava solto), e via-se o nome ora com
// ora sem pontuacao consoante o que tinha sido enviado.
//
// Os dois pontos entram aqui, e nao no template, para que `displaySenderName`
// continue sendo o NOME — ele tambem alimenta comparacoes e rotulos noutros
// pontos, e um nome com pontuacao colada quebraria essas leituras. O
// documento e o audio desenham o rotulo por conta propria (File.vue e
// AudioPresentation.vue) e repetem la o mesmo formato, pela mesma razao.
const senderNameLabel = computed(() =>
  displaySenderName.value ? `${displaySenderName.value}:` : displaySenderName.value
);

const groupSenderColor = computed(() => {
  if (participantColor.value) return participantColor.value;
  if (props.showSenderName || props.textSenderName) return '!text-[#000]';

  return 'text-n-slate-11';
});

// Aceleres: o marcador "Encaminhada" pertence somente à mensagem de texto.
// Os demais tipos preservam `forwarded` para rastreabilidade e envio, mas não
// recebem uma faixa que altere a composição própria da mídia ou figurinha.
const isForwardedText = computed(
  () =>
    !!contentAttributes.value?.forwarded &&
    attrs['data-bubble-name'] === 'text'
);

const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-[#d9fdd3] text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    '!bg-n-solid-amber text-n-amber-12 shadow-sm ring-1 ring-inset ring-n-amber-7/50 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-white text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]: 'left-bubble rounded-xl',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    classToApply.push(
      'acl-private-note-bubble [&_.acl-media-caption-surface]:!bg-[#fcefc3]'
    );
  }

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

// Aceleres: sticker tem fundo transparente e o MessageMeta (horario) e
// sobreposto por cima da propria imagem — ver Image.vue, que emite este
// mesmo `data-bubble-name`. Usado abaixo pra colar o horario na quina do
// sticker (ver comentario no template) e no CSS de espacamento entre
// stickers (Message.vue, seletor `[data-bubble-name='sticker']`).
const isStickerBubble = computed(() => attrs['data-bubble-name'] === 'sticker');
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const isMobileSticker = computed(
  () => isStickerBubble.value && isMobileViewport.value
);

// Aceleres: no documento (attachment) o horario nao fica sobreposto ao
// fundo tingido do painel (icone+nome+tipo) — flui como linha propria
// abaixo dele, fora do .acl-file-bubble__body (ver File.vue).
const isFileBubble = computed(() => attrs['data-bubble-name'] === 'attachment');
const isPrivateTextBubble = computed(
  () =>
    attrs['data-bubble-name'] === 'text' &&
    variant.value === MESSAGE_VARIANTS.PRIVATE
);

const textBubbleClass = computed(() => {
  if (attrs['data-bubble-name'] !== 'text') return [];

  const classes = [];
  // O fundo verde também cobre envios de bot e modelo, além dos agentes.
  if (
    variant.value === MESSAGE_VARIANTS.USER ||
    (orientation.value === ORIENTATION.RIGHT &&
      ![MESSAGE_VARIANTS.PRIVATE, MESSAGE_VARIANTS.EMAIL].includes(variant.value))
  ) {
    classes.push('shadow-[0_1px_0_#d1d1d1]');
  }

  if (
    orientation.value === ORIENTATION.LEFT &&
    variant.value !== MESSAGE_VARIANTS.PRIVATE
  ) {
    classes.push(
      "[&.left-bubble[data-bubble-name='text'].acl-text-bubble]:!p-[6px_0_6px_8px]"
    );
  }

  if (
    orientation.value === ORIENTATION.RIGHT &&
    variant.value !== MESSAGE_VARIANTS.PRIVATE
  ) {
    classes.push(
      "[&.right-bubble[data-bubble-name='text'].acl-text-bubble]:!p-[6px_15px_6px_8px]"
    );
  }

  return classes;
});

// Aceleres: a bolha de texto tem padding lateral assimétrico (ver
// textBubbleClass acima: 8px à esquerda e 15px à direita no envio, 0 à direita
// no recebimento). O bloco citado é o único filho que ocupa a largura inteira,
// então essa assimetria vira um vão à direita — o fundo da citação parece
// parar antes do fim da bolha, em vez de ir até a borda com o mesmo respiro
// dos dois lados, como no WhatsApp.
//
// A margem da direita é escrita aqui inteira, e não somada a um `-mx-*` no
// template, porque duas utilitárias mexendo na mesma margem dependeriam da
// ordem em que o Tailwind as emite. O alvo é sempre 6px de respiro visível:
// a esquerda já tem (8px de padding menos os 2px da margem negativa).
const quotedRightInsetClass = computed(() => {
  if (attrs['data-bubble-name'] !== 'text') return '-mr-0.5';
  // Envio: 15px de padding, então -9px deixam os 6px.
  if (orientation.value === ORIENTATION.RIGHT) return '-mr-[9px]';
  // Recebimento: padding-right zero — a citação chegava colada na borda (e
  // ainda vazava os 2px da margem negativa), então aqui a margem é positiva.
  if (orientation.value === ORIENTATION.LEFT) return 'mr-1.5';
  return '-mr-0.5';
});

// Aceleres: clique no preview de reply-to faz scroll/highlight da mensagem
// original (estilo WhatsApp: tap no quote → vai pra mensagem citada).
const scrollToMessage = event => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
  if (!inReplyTo?.value?.id) return;
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () => !props.hideMeta && variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// Aceleres 2026-08-19: o horario e absoluto e o conteudo reserva 8px a
// direita (`pr-2`) — o mesmo respiro que ja existe a esquerda. A condicao vive
// aqui, e nao solta no template, porque a compensacao logo abaixo TEM de usar
// exatamente a mesma — se as duas divergirem, o cartao de link sai da bolha.
const metaReservesSpace = computed(
  () =>
    shouldShowMeta.value &&
    variant.value !== MESSAGE_VARIANTS.EMAIL &&
    !props.mediaCaption
);

// A faixa por baixo do conteudo. Continua a ser o caminho de todas as bolhas
// que nao sabem abrir lugar na linha — audio, contato, localizacao, CSAT — e
// tambem da propria bolha de texto quando ela tem mais coisas depois do texto
// (badge de "editada", tradutor, chips), porque ai o fim do conteudo ja nao e
// o fim da ultima linha e o espacador nao reservaria nada.
const metaReservesBand = computed(
  () => metaReservesSpace.value && !props.inlineMetaSpacer
);

// Aceleres 2026-08-19: o horario e o vao que o conteudo abre para ele TEM de
// fechar a mesma conta, senao ele assenta por cima da ultima linha — era o que
// acontecia na mensagem curta ("Teste" com o 14:57 atravessado no meio).
//
// A conta e `bottom + altura do horario <= reserva + padding de baixo da
// bolha`: o horario mede 1rem (`leading-4` no MessageMeta), a reserva acima e
// 1.25rem (`mb-5`, logo abaixo) e o padding de baixo da bolha de texto 6px
// (textBubbleClass) — com `bottom-1` (0.25rem) sobram 6px de folga.
//
// Tudo em rem de proposito: o tamanho da fonte da raiz muda por utilizador
// (useFontSize.js) e uma reserva em pixel fixo voltaria a ficar curta noutro
// tamanho. Medido no repro com 16px e com 20px de raiz.
//
// So vale onde HA reserva. Na midia com legenda o horario assenta na faixa da
// legenda, que tem a folga propria dela (aceleres-overrides.css, "a vaga do
// horario"), e a figurinha cola na quina (`bottom-0`, ver o comentario no
// template).
const metaBottomClass = computed(() => {
  if (isStickerBubble.value) return 'bottom-0';

  return metaReservesSpace.value ? 'bottom-1' : 'bottom-2.5';
});

// Aceleres 2026-08-18 -- a COR do cartao de previa de link, que segue a bolha.
//
// No WhatsApp o cartao e sempre um tom mais escuro do que a bolha onde assenta:
// no envio a bolha e `#d9fdd3` e o cartao `#d5f3cf`. Na recepcao a bolha e
// BRANCA (variante USER, `bg-white`), portanto o tom acima dela e um cinza
// claro — nao o verde. O cartao trazia `bg-[#d5f3cf]` fixo no proprio
// LinkPreview.vue, entao o link recebido aparecia verde dentro de uma bolha
// branca. A cor mudou-se para aqui porque so este componente sabe de que lado
// a mensagem esta.
//
// Vai pelo gancho `.acl-link-preview`. A variante arbitraria gera um seletor
// de DUAS classes (a do pai mais a do cartao), logo
// tem prioridade sobre qualquer utilitario de fundo escrito no proprio cartao —
// mas o fundo de la foi retirado, para nao haver dois donos da mesma cor.
//
// As duas classes estao escritas por extenso porque o Tailwind so gera o CSS
// das strings que consegue ler no fonte, e uma classe montada por concatenacao
// nao existiria na folha final.
const linkPreviewToneClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT
    ? '[&_.acl-link-preview]:bg-[#d5f3cf]'
    : '[&_.acl-link-preview]:bg-[#f5f6f6]'
);

// Autor, cor e o que mostrar no lugar do conteúdo saem do mesmo helper que o
// card do compositor usa — assim a citação é igual nos dois lugares.
const quoted = computed(() =>
  inReplyTo?.value ? describeQuotedMessage(inReplyTo.value, t) : null
);

// O ícone da mídia acompanha SEMPRE a citação, tenha ela legenda ou não —
// é assim no WhatsApp. Antes ele sumia quando havia texto, e era por isso que
// um vídeo com legenda aparecia sem ícone nenhum.
const quotedMediaKind = computed(() => quoted.value?.kind || '');

// A citação enviada mostra o nome da conta ativa no atendimento.
const quotedAuthorName = computed(() =>
  quoted.value?.isOutgoing
    ? currentAccount.value?.name || quoted.value.authorName
    : quoted.value?.authorName || ''
);

const quotedMediaTitle = computed(() =>
  quotedMediaKind.value ? quoted.value?.label || '' : ''
);

// A legenda da mensagem citada, embaixo da linha do ícone — e, na citação de
// texto puro, o texto inteiro. Vazia quando a mídia não tem legenda: aí a
// linha do ícone já diz tudo.
const replyToCaption = computed(() => {
  if (!inReplyTo?.value) return '';
  // A figurinha vale pelo desenho, não por um rótulo repetido.
  if (quoted.value?.isSticker) return '';

  const attributes =
    inReplyTo.value.contentAttributes ??
    inReplyTo.value.content_attributes ??
    {};
  const content = stripGroupSenderPrefix(inReplyTo.value.content, attributes);
  if (!content) return '';

  return new MessageFormatter(content).formattedMessage;
});

// Aceleres 2026-08-24: resposta a story do Instagram. O WameIncomingMessage
// grava `instagram_story_url` / `instagram_story_id` em content_attributes
// (o story NÃO é mensagem, por isso não vira `in_reply_to`), mas até aqui
// ninguém no painel lia essas chaves — o vendedor via "que top!" sem saber a
// que story o cliente respondia. O bloco desenhado abaixo usa o mesmo desenho
// da citação.
const STORY_PERMALINK_HOSTS = [
  'instagram.com',
  'www.instagram.com',
  'facebook.com',
  'www.facebook.com',
  'fb.watch',
];

// A resolução da mídia passa pelo composable único: uma URL do endpoint
// guardado (`/attachment_downloads/...`) levaria 401 num `<img src>` direto, e
// uma URL externa (CDN do Instagram) sai daqui intacta.
const { guardedMediaSrc } = useGuardedMedia();

const storyThumbFailed = ref(false);
const storyMediaType = ref('');
const storyMediaUnavailable = ref(false);
const storyOpenPending = ref(false);
const showStoryGallery = ref(false);

const storyReference = computed(() => {
  const attributes = contentAttributes?.value ?? {};
  const url = String(
    attributes.instagramStoryUrl ?? attributes.instagram_story_url ?? ''
  );
  const storyId = String(
    attributes.instagramStoryId ?? attributes.instagram_story_id ?? ''
  );
  if (!url && !storyId) return null;

  let isPermalink = false;
  try {
    isPermalink = url
      ? STORY_PERMALINK_HOSTS.includes(new URL(url).host.toLowerCase())
      : false;
  } catch {
    isPermalink = false;
  }

  return { url, id: storyId, isPermalink };
});

// Miniatura só quando a URL aponta para o arquivo do story. Quando ela é um
// permalink (uma PÁGINA do Instagram), desenhar `<img>` daria um quadrado
// quebrado — aí fica só o link.
const storyThumbUrl = computed(() => {
  const reference = storyReference.value;
  if (!reference?.url || reference.isPermalink || storyThumbFailed.value) {
    return '';
  }

  return guardedMediaSrc(reference.url);
});

const storyAttachment = computed(() => {
  const reference = storyReference.value;
  if (!reference?.url || reference.isPermalink || !storyMediaType.value) {
    return null;
  }

  return {
    id: `instagram-story-${messageId.value}`,
    message_id: messageId.value,
    file_type: storyMediaType.value,
    data_url: reference.url,
    thumb_url: storyMediaType.value === 'image' ? reference.url : '',
    created_at: createdAt.value,
    sender: sender.value,
    aceleresStoryReference: true,
  };
});

const abrirStoryQuandoPronto = () => {
  if (!storyOpenPending.value || !storyAttachment.value) return;
  storyOpenPending.value = false;
  showStoryGallery.value = true;
};

const onStoryImageLoad = () => {
  storyMediaUnavailable.value = false;
  storyMediaType.value = 'image';
  abrirStoryQuandoPronto();
};

const onStoryImageError = () => {
  storyThumbFailed.value = true;
  storyMediaUnavailable.value = false;
  storyMediaType.value = 'video';
  abrirStoryQuandoPronto();
};

const onStoryVideoError = () => {
  storyMediaUnavailable.value = true;
  storyOpenPending.value = false;
};

const openStoryGallery = () => {
  if (
    !storyReference.value?.url ||
    storyReference.value.isPermalink ||
    storyMediaUnavailable.value
  ) {
    return;
  }
  if (storyAttachment.value) {
    showStoryGallery.value = true;
    return;
  }

  // O navegador identifica o tipo pelo proprio recurso: a tentativa como
  // imagem acontece primeiro e, se falhar, o mesmo URL passa ao <video>.
  storyOpenPending.value = true;
};

const storyReferenceHint = computed(() => {
  const reference = storyReference.value;
  if (!reference) return '';
  if (reference.url && !reference.isPermalink && !storyMediaUnavailable.value) {
    return 'Abrir o story';
  }

  return 'Story indisponível';
});

// Mensagem citada que o painel não conseguiu carregar: sem ícone, sem título
// e sem legenda, o bloco ficaria vazio.
const replyToFallback = computed(() =>
  quotedMediaKind.value || quotedMediaTitle.value || replyToCaption.value
    ? ''
    : t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND')
);
</script>

<template>
  <div
    v-twemoji
    v-bind="attrs"
    class="text-[14px] leading-[19px] focus:outline-none [&_.cw-emoji]:inline-block [&_.cw-emoji--with-gap]:mr-px"
    :class="[
      messageClass,
      textBubbleClass,
      isPrivateTextBubble ? 'acl-private-text-note !rounded-xl !border !border-n-amber-7/60 !p-2.5 !pb-7 after:!hidden' : '',
      {
        'min-w-[4.5rem] max-w-[min(85vw,42rem)] sm:max-w-[min(65vw,42rem)]':
          variant !== MESSAGE_VARIANTS.EMAIL,
        'overflow-hidden': isAttachmentReply,
        relative: variant !== MESSAGE_VARIANTS.EMAIL && shouldShowMeta,
        'pb-4':
          variant !== MESSAGE_VARIANTS.EMAIL &&
          shouldShowMeta &&
          !props.mediaCaption,
      },
    ]"
  >
    <svg
      v-if="isPrivateTextBubble && orientation === ORIENTATION.RIGHT"
      class="acl-private-text-note__tip pointer-events-none absolute -right-3 top-0 z-[3] h-3 w-6"
      viewBox="0 0 24 12"
      aria-hidden="true"
    >
      <path d="M0 0H24V1.5L12 12Z" fill="rgb(var(--solid-amber))" />
      <path
        d="M0 0H24V1.5L12 12"
        fill="none"
        stroke="rgb(var(--amber-7) / 0.6)"
        stroke-width="1"
        stroke-linejoin="round"
      />
    </svg>
    <!-- Aceleres: referência ao story respondido (Instagram via WAME). -->
    <button
      v-if="storyReference"
      type="button"
      class="acl-story-reference -ml-0.5 mb-1.5 flex w-full items-stretch overflow-hidden rounded-md border-l-4 border-n-weak bg-[rgba(194,189,184,0.15)] p-0 text-left no-underline"
      :class="{
        'cursor-pointer': storyReference.url && !storyReference.isPermalink,
        'cursor-default': !storyReference.url || storyReference.isPermalink,
      }"
      @click.stop="openStoryGallery"
    >
      <div class="min-w-0 flex-1 px-2 py-1.5">
        <div class="flex min-w-0 items-center gap-1.5">
          <span
            class="i-lucide-circle-play shrink-0 text-n-slate-11"
            aria-hidden="true"
          />
          <span class="truncate font-semibold">Respondeu ao story</span>
        </div>
        <span class="text-n-slate-11">{{ storyReferenceHint }}</span>
      </div>
      <img
        v-if="storyThumbUrl"
        :src="storyThumbUrl"
        alt="Story respondido"
        class="w-[62px] flex-shrink-0 self-stretch object-cover"
        @load="onStoryImageLoad"
        @error="onStoryImageError"
      />
      <video
        v-else-if="storyMediaType === 'video'"
        :src="storyReference.url"
        class="w-[62px] flex-shrink-0 self-stretch object-cover"
        muted
        playsinline
        preload="metadata"
        aria-hidden="true"
        @loadedmetadata="storyMediaUnavailable = false"
        @error="onStoryVideoError"
      />
    </button>
    <!-- No sticker, min-w-0 acionaria fit-content na regra antiga;
         com contain:inline-size a citação ficaria só com a borda. -->
    <div
      v-if="inReplyTo"
      class="-ml-0.5 mb-1.5 cursor-pointer overflow-hidden rounded-md border-l-4 acl-reply-to-preview"
      :class="[
        quotedRightInsetClass,
        {
          '!mx-0 box-border w-full max-w-full [contain:inline-size]':
            isAttachmentReply && !isFileBubble,
          'min-w-0':
            isAttachmentReply && !isFileBubble && !isStickerBubble,
          'acl-sticker-reply': isStickerBubble,
          'bg-[#d5f3cf]': isStickerBubble && orientation === ORIENTATION.RIGHT,
          '!rounded-tr-none':
            isStickerBubble && orientation === ORIENTATION.RIGHT,
          'bg-[#f5f6f6]': isStickerBubble && orientation !== ORIENTATION.RIGHT,
          'bg-[rgba(194,189,184,0.15)]': !isStickerBubble,
          '!mx-1.5 mt-1.5 shrink-0': isFileBubble,
        },
      ]"
      :style="{ borderLeftColor: quoted?.accentColor }"
      role="button"
      tabindex="0"
      @click="scrollToMessage"
      @keydown.enter.space.prevent="scrollToMessage"
    >
      <!-- O bloco acompanha a largura do anexo enviado; a citação não alarga a bolha. -->
      <div class="flex items-stretch">
        <div
          class="min-w-0 flex-1 overflow-hidden px-2"
          :class="[
            isAttachmentReply ? 'py-1 text-[13px] leading-4' : 'py-1.5',
            { '!pr-10': isStickerBubble },
          ]"
        >
          <div
            v-if="quotedAuthorName && quotedAuthorName !== senderNameAbove"
            class="mb-0.5 truncate font-semibold text-[#14532D]"
          >
            {{ quotedAuthorName }}
          </div>
          <!-- Respostas com anexo usam uma citação compacta, sem outra mídia. -->
          <LottieStickerCanvas
            v-if="!isAttachmentReply && quoted?.stickerLottieUrl"
            :key="quoted.stickerLottieUrl"
            :url="quoted.stickerLottieUrl"
            class="size-12"
          />
          <img
            v-else-if="!isAttachmentReply && quoted?.stickerUrl"
            :src="quoted.stickerUrl"
            :alt="quoted.label"
            class="size-12 object-contain"
          />
          <template v-else>
            <div
              v-if="quotedMediaKind || quotedMediaTitle"
              class="flex min-w-0 items-center gap-1.5"
            >
              <QuotedMediaIcon
                v-if="quotedMediaKind"
                :kind="quotedMediaKind"
                :icon="quoted?.icon"
                :class="{ '!size-4': isAttachmentReply }"
              />
              <!-- O nome fica no título; a descrição do anexo usa peso normal. -->
              <span v-if="quotedMediaTitle" class="min-w-0 flex-1 truncate">
                {{ quotedMediaTitle }}
              </span>
            </div>
            <div
              v-if="replyToCaption"
              v-dompurify-html="replyToCaption"
              v-twemoji
              class="prose prose-bubble line-clamp-2 break-words"
            />
            <span v-else-if="replyToFallback">{{ replyToFallback }}</span>
          </template>
        </div>
        <!-- O MP4 mudo que o WhatsApp usa como GIF só vira miniatura pelo
             próprio vídeo quando o servidor não conseguiu gerar a prévia. -->
        <video
          v-if="
            !isAttachmentReply &&
            quoted?.mediaThumbUrl &&
            quoted?.mediaThumbIsVideo
          "
          :src="quoted.mediaThumbUrl"
          class="w-[62px] flex-shrink-0 self-stretch object-cover"
          muted
          playsinline
          preload="metadata"
          aria-hidden="true"
        />
        <img
          v-else-if="!isAttachmentReply && quoted?.mediaThumbUrl"
          :src="quoted.mediaThumbUrl"
          :alt="quoted.label"
          class="w-[62px] flex-shrink-0 self-stretch object-cover"
        />
      </div>
    </div>
    <span
      v-if="inReplyTo && isStickerBubble && orientation === ORIENTATION.RIGHT"
      class="pointer-events-none absolute -right-3 top-0 z-[3] h-0 w-0 border-r-[12px] border-r-transparent border-t-[12px] border-t-[#d5f3cf]"
      aria-hidden="true"
    />
    <div
      v-if="shouldShowSenderName && !hideSenderName"
      class="acl-sender-name mb-1 leading-tight opacity-90"
      :class="[
        groupSenderColor,
        senderNameClass,
        {
          'acl-media-sender': props.showSenderName && !isMobileSticker,
          'acl-sticker-sender-mobile relative !left-0 !top-0 !h-auto !max-h-none !w-full !max-w-full !overflow-visible !whitespace-normal !break-words !text-clip !rounded-lg !px-3 !py-2':
            isMobileSticker,
          '!bg-[#d5f3cf]': isMobileSticker && orientation === ORIENTATION.RIGHT,
          '!bg-[#f5f6f6]': isMobileSticker && orientation !== ORIENTATION.RIGHT,
          'w-max max-w-full truncate': props.textSenderName,
          'pr-2': props.textSenderName && orientation === ORIENTATION.LEFT,
        },
      ]"
    >
      {{ senderNameLabel }}
    </div>
    <!-- Aceleres: rótulo "Encaminhada" apenas na mensagem de texto. -->
    <div
      v-if="isForwardedText"
      class="mb-1 flex items-center gap-1 text-[11px] font-medium leading-tight text-n-slate-11/80 acl-forwarded-label"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        style="flex-shrink: 0"
        aria-hidden="true"
      >
        <polyline points="15 14 20 9 15 4" />
        <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
      </svg>
      <span>Encaminhada</span>
    </div>
    <slot name="before-private-note" />
    <div
      v-if="variant === MESSAGE_VARIANTS.PRIVATE"
      class="acl-private-note-heading relative mb-2 flex w-full items-center gap-1.5 border-n-amber-7/60 bg-[#fcefc3] px-2 py-2 font-bold uppercase tracking-normal text-n-amber-12 md:text-xs max-md:flex-wrap max-md:text-[12px] max-md:leading-4 max-md:opacity-100 after:absolute after:bottom-0 after:left-2 after:right-2 after:border-b after:border-n-amber-7/60 after:content-['']"
      :class="{ 'acl-private-document-heading': isFileBubble, 'acl-private-text-note__heading !mb-0 !rounded-t-xl !border-x !border-t max-md:!flex-nowrap max-md:!gap-1 max-md:!px-1.5': isPrivateTextBubble }"
    >
      <span
        class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-n-amber-4 px-2 py-1 !text-[#79580d]"
        :class="{ 'max-md:!gap-0.5 max-md:!px-1 max-md:!text-[11px]': isPrivateTextBubble }"
      >
        <span class="i-ph-note-pencil size-3.5" aria-hidden="true" />
        {{ t('CONVERSATION.REPLYBOX.INTERNAL_NOTE_BADGE') }}
      </span>
      <span
        class="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium normal-case tracking-normal text-n-amber-11"
        :class="{ 'acl-private-text-note__team-mobile max-md:!gap-0.5 max-md:!text-[11px]': isPrivateTextBubble }"
        :aria-label="isPrivateTextBubble ? t('CONVERSATION.REPLYBOX.TEAM_ONLY') : undefined"
      >
        <span class="i-lucide-lock-keyhole size-3" aria-hidden="true" />
        <span :class="{ 'max-[360px]:hidden': isPrivateTextBubble }">
          {{ t('CONVERSATION.REPLYBOX.TEAM_ONLY') }}
        </span>
      </span>
    </div>
    <!-- Aceleres 2026-09-02: no cartao de documento este invólucro precisa de
         nome. A bolha tem altura fixa (336x194, ver File.vue) e e um flex
         column cujos filhos sao ESTE div e o horario; sem uma classe para o
         alcancar, ele fica com altura livre, empurra o horario para fora dos
         194px e o `overflow: hidden` da bolha o corta. -->
    <div
      :class="[
        metaReservesBand ? 'pr-2 mb-5' : '',
        linkPreviewToneClass,
        isFileBubble ? 'acl-file-bubble__content' : '',
        isFileBubble && variant === MESSAGE_VARIANTS.PRIVATE ? '!flex-none' : '',
        isPrivateTextBubble ? 'acl-private-text-note__content !rounded-b-xl !border !border-t-0 !border-n-amber-7/60 !bg-[#fcefc3] !px-3 !py-2.5' : '',
      ]"
      class="min-w-0 break-words"
    >
      <slot />
    </div>
    <!-- Aceleres: no sticker o horario cola na quina inferior (bottom-0) em
         vez do bottom-2.5 padrao das outras midias — o proprio sticker ja
         costuma trazer respiro transparente embutido no webp, e colar o
         horario na borda real e o que deixa o espacamento de 12px entre
         stickers (Message.vue) contado a partir do horario batendo certo,
         sem sobrepor a bolha seguinte. So se aplica ao sticker: foto/video
         mantem o bottom-2.5 de sempre. -->
    <MessageMeta
      v-if="shouldShowMeta"
      :status-on-media="statusOnMedia"
      :class="[
        // Aceleres 2026-09-01: nome estavel para o bloco do horario. Ate aqui
        // a unica forma de o apanhar de fora era pela dupla de utilitarios
        // `absolute right-2`, que muda com o desenho.
        'acl-bubble-meta',
        'justify-end',
        variant === MESSAGE_VARIANTS.EMAIL
          ? 'mt-2 px-3 pb-3'
          : isFileBubble
            ? // Aceleres 2026-09-02: no cartao de documento o horario tem
              // nome proprio (`acl-file-bubble__meta`) e nao leva respiro em
              // cima — ele ja vem logo abaixo do painel do arquivo, que tem o
              // seu proprio padding.
              ['acl-file-bubble__meta', 'px-2', 'pb-2']
            : ['absolute', 'right-2', metaBottomClass],
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
    />
  </div>
  <GalleryView
    v-if="showStoryGallery && storyAttachment"
    v-model:show="showStoryGallery"
    :attachment="storyAttachment"
    :all-attachments="[storyAttachment]"
    @error="storyMediaUnavailable = true"
    @close="showStoryGallery = false"
  />
</template>
